import React, { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tab,
} from '@mui/material';

function planViewBox(links) {
  if (!links?.length) return { minX: 0, maxX: 12, minY: 0, maxY: 12, w: 12, h: 12 };
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const lk of links) {
    for (const k of ['x0_m', 'x1_m']) {
      minX = Math.min(minX, lk[k]);
      maxX = Math.max(maxX, lk[k]);
    }
    for (const k of ['y0_m', 'y1_m']) {
      minY = Math.min(minY, lk[k]);
      maxY = Math.max(maxY, lk[k]);
    }
  }
  const pad = 1.5;
  return {
    minX: minX - pad,
    maxX: maxX + pad,
    minY: minY - pad,
    maxY: maxY + pad,
    w: maxX - minX + 2 * pad,
    h: maxY - minY + 2 * pad,
  };
}

function elevViewBox(links) {
  if (!links?.length) return { minX: 0, maxX: 12, minZ: 0, maxZ: 6, w: 12, h: 6 };
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = 0;
  let maxZ = -Infinity;
  for (const lk of links) {
    minX = Math.min(minX, lk.x0_m, lk.x1_m);
    maxX = Math.max(maxX, lk.x0_m, lk.x1_m);
    minZ = Math.min(minZ, lk.z0_m, lk.z1_m);
    maxZ = Math.max(maxZ, lk.z0_m, lk.z1_m);
  }
  const pad = 1;
  return {
    minX: minX - pad,
    maxX: maxX + pad,
    minZ: minZ - pad,
    maxZ: maxZ + pad,
    w: maxX - minX + 2 * pad,
    h: maxZ - minZ + 2 * pad,
  };
}

function strokeForGroup(group) {
  if (group === 'lateral_support_longitudinal') return '#c2185b';
  if (group?.includes('longitudinal')) return '#2e7d32';
  if (group?.includes('transverse')) return '#1565c0';
  if (group?.includes('vertical')) return '#ef6c00';
  if (group === 'wind_column') return '#7b1fa2';
  return '#616161';
}

function fmtForce(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return Number(n).toFixed(2);
}

/** Full-width canvas; world metres mapped so stroke/font stay readable. */
const DIAG_W = 900;
const DIAG_H = 520;

function LinkDiagram({ links, mode = 'plan', title, forces = [], showAxes = false }) {
  const vb = useMemo(() => (mode === 'plan' ? planViewBox(links) : elevViewBox(links)), [links, mode]);
  const forceByBay = useMemo(() => {
    const m = new Map();
    for (const f of forces) {
      const bi = f.bay_index ?? f.global_i;
      if (bi != null) m.set(bi, f.N_kN);
    }
    return m;
  }, [forces]);

  const scale = useMemo(() => {
    const sx = DIAG_W / Math.max(vb.w, 1e-6);
    const sy = DIAG_H / Math.max(vb.h, 1e-6);
    const s = Math.min(sx, sy);
    return {
      s,
      ox: (DIAG_W - vb.w * s) / 2,
      oy: (DIAG_H - vb.h * s) / 2,
    };
  }, [vb]);

  if (!links?.length) {
    return (
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Typography variant="subtitle2" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No links in this load path.
        </Typography>
      </Paper>
    );
  }

  // Plan: X right, Y up (building Y increases toward top of SVG). Elev: X right, Z up.
  const worldY = (lk, end) => {
    if (mode === 'plan') {
      const y = end === 0 ? lk.y0_m : lk.y1_m;
      return y - vb.minY;
    }
    const z = end === 0 ? lk.z0_m : lk.z1_m;
    return z - vb.minZ;
  };
  const toScreen = (xWorld, yWorldUp) => ({
    x: scale.ox + xWorld * scale.s,
    y: scale.oy + (vb.h - yWorldUp) * scale.s,
  });

  const ax0 = toScreen(0.4, 0.4);
  const axX = toScreen(2.4, 0.4);
  const axY = toScreen(0.4, 2.4);

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="subtitle2" gutterBottom>
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {mode === 'plan'
          ? 'Plan: X → span (right), Y → length (up). Green: longitudinal HT · Blue: transverse VV · Pink: lateral support'
          : 'Elevation: X → span (right), Z → up. Orange: vertical braces · Purple: wind columns'}
      </Typography>
      <svg
        viewBox={`0 0 ${DIAG_W} ${DIAG_H}`}
        width="100%"
        style={{ maxHeight: 560, display: 'block', background: '#fafafa' }}
      >
        {links.map((lk) => {
          const x0w = lk.x0_m - vb.minX;
          const x1w = lk.x1_m - vb.minX;
          const y0w = worldY(lk, 0);
          const y1w = worldY(lk, 1);
          const p0 = toScreen(x0w, y0w);
          const p1 = toScreen(x1w, y1w);
          const n = lk.N_kN ?? (lk.bay_index != null ? forceByBay.get(lk.bay_index) : null);
          const isLat = lk.group === 'lateral_support_longitudinal';
          return (
            <g key={lk.link_id}>
              <line
                x1={p0.x}
                y1={p0.y}
                x2={p1.x}
                y2={p1.y}
                stroke={strokeForGroup(lk.group)}
                strokeWidth={isLat ? 1.6 : lk.group?.includes('collector') ? 3.4 : 2.4}
                strokeLinecap="round"
                strokeDasharray={isLat ? '6 4' : undefined}
                opacity={isLat ? 0.85 : 1}
              />
              {n != null && Math.abs(n) > 0.01 && !isLat && (
                <text
                  x={(p0.x + p1.x) / 2}
                  y={(p0.y + p1.y) / 2 - 6}
                  fontSize={11}
                  fill="#212121"
                  textAnchor="middle"
                  stroke="#fff"
                  strokeWidth={3}
                  paintOrder="stroke"
                >
                  {Number(n).toFixed(1)} kN
                </text>
              )}
            </g>
          );
        })}
        {showAxes && mode === 'plan' && (
          <g>
            <line x1={ax0.x} y1={ax0.y} x2={axX.x} y2={axX.y} stroke="#1b5e20" strokeWidth={2.5} />
            <line x1={ax0.x} y1={ax0.y} x2={axY.x} y2={axY.y} stroke="#1b5e20" strokeWidth={2.5} />
            <polygon
              points={`${axX.x},${axX.y} ${axX.x - 8},${axX.y - 4} ${axX.x - 8},${axX.y + 4}`}
              fill="#1b5e20"
            />
            <polygon
              points={`${axY.x},${axY.y} ${axY.x - 4},${axY.y + 8} ${axY.x + 4},${axY.y + 8}`}
              fill="#1b5e20"
            />
            <text x={axX.x + 6} y={axX.y + 4} fontSize={14} fontWeight={700} fill="#1b5e20">
              X
            </text>
            <text x={axY.x - 4} y={axY.y - 6} fontSize={14} fontWeight={700} fill="#1b5e20">
              Y
            </text>
          </g>
        )}
      </svg>
    </Paper>
  );
}

function ForceTable({ title, rows, columns }) {
  if (!rows?.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        {title}: no force results yet.
      </Typography>
    );
  }
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        {title}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((c) => (
              <TableCell key={c.key} align={c.align || 'left'}>
                {c.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={row.key || i}>
              {columns.map((c) => (
                <TableCell key={c.key} align={c.align || 'left'}>
                  {c.render ? c.render(row) : row[c.key]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 1.5 }}>{children}</Box>;
}

export default function BracingLoadPathsView({ results }) {
  const [subTab, setSubTab] = useState(0);
  const hDiag = results?.load_path_diagrams?.horizontal;
  const vDiag = results?.load_path_diagrams?.vertical;
  const needs = results?.bracing_needs;
  const stab = results?.bracing_stabilizing;

  const collectorRows = useMemo(() => {
    const rows = hDiag?.collector_forces || [];
    return rows.map((f, i) => ({
      key: `c-${i}`,
      bay: f.bay_index ?? f.global_i ?? '—',
      group: f.group || 'roof_collector',
      L_m: f.L_m,
      N_kN: f.N_kN,
    }));
  }, [hDiag]);

  const braceRows = useMemo(() => {
    const rows = hDiag?.brace_forces || [];
    return rows.map((f, i) => ({
      key: `b-${i}`,
      bay: f.bay_index ?? '—',
      group: f.group || 'roof_brace',
      L_m: f.L_diag_m ?? f.L_m,
      N_kN: f.N_kN,
    }));
  }, [hDiag]);

  const vbRows = useMemo(() => {
    const fromDiag = vDiag?.vertical_brace_forces || [];
    if (fromDiag.length) {
      return fromDiag.map((f, i) => ({
        key: f.link_id || `vb-${i}`,
        link_id: f.link_id || '—',
        group: f.group || '—',
        bay: f.bay_index ?? f.frame_i ?? '—',
        N_kN: f.N_kN,
      }));
    }
    return (vDiag?.links || [])
      .filter((lk) => String(lk.group || '').startsWith('vertical_brace'))
      .map((lk, i) => ({
        key: lk.link_id || `vb-${i}`,
        link_id: lk.link_id || '—',
        group: lk.group || '—',
        bay: lk.bay_index ?? lk.frame_i ?? '—',
        N_kN: lk.N_kN,
      }));
  }, [vDiag]);

  if (!hDiag && !vDiag) {
    return (
      <Typography variant="body2" color="text.secondary">
        Run whole-building analysis to view bracing load paths.
      </Typography>
    );
  }

  return (
    <Box>
      {needs && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Layout: {needs.layout_mode || '—'} · links: {needs.num_bracing_links ?? '—'}
          {needs.stabilizing_actions?.delta_q_over_e0 != null &&
            ` · δq/e₀ = ${Number(needs.stabilizing_actions.delta_q_over_e0).toFixed(3)} (${needs.stabilizing_actions.stiffness_gate || '—'})`}
        </Typography>
      )}
      <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
        <Tab label="Horizontal bracing" />
        <Tab label="Vertical bracing" />
      </Tabs>
      <TabPanel value={subTab} index={0}>
        <LinkDiagram
          title={hDiag?.title || 'Horizontal bracing'}
          links={hDiag?.links}
          mode="plan"
          showAxes
          forces={[...(hDiag?.collector_forces || []), ...(hDiag?.brace_forces || [])]}
        />
        <ForceTable
          title="Collector forces"
          rows={collectorRows}
          columns={[
            { key: 'bay', label: 'Bay / frame' },
            { key: 'group', label: 'Group' },
            {
              key: 'L_m',
              label: 'L (m)',
              align: 'right',
              render: (r) => (r.L_m != null ? Number(r.L_m).toFixed(2) : '—'),
            },
            {
              key: 'N_kN',
              label: 'N (kN)',
              align: 'right',
              render: (r) => fmtForce(r.N_kN),
            },
          ]}
        />
        <ForceTable
          title="Horizontal brace forces"
          rows={braceRows}
          columns={[
            { key: 'bay', label: 'Bay' },
            { key: 'group', label: 'Group' },
            {
              key: 'L_m',
              label: 'L (m)',
              align: 'right',
              render: (r) => (r.L_m != null ? Number(r.L_m).toFixed(2) : '—'),
            },
            {
              key: 'N_kN',
              label: 'N (kN)',
              align: 'right',
              render: (r) => fmtForce(r.N_kN),
            },
          ]}
        />
        {stab?.ok && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
            §5.3.3 (stabilizing on horizontal system): e₀ = {Number(stab.e0_m || 0).toFixed(4)} m · δq ={' '}
            {Number(stab.delta_q_m || 0).toFixed(4)} m · N_Ed (governing chord) = {Number(stab.n_ed_kn || 0).toFixed(1)}{' '}
            kN
          </Typography>
        )}
      </TabPanel>
      <TabPanel value={subTab} index={1}>
        <LinkDiagram title={vDiag?.title || 'Vertical bracing'} links={vDiag?.links} mode="elev" />
        <ForceTable
          title="Vertical brace forces"
          rows={vbRows}
          columns={[
            { key: 'link_id', label: 'Link' },
            { key: 'group', label: 'Group' },
            { key: 'bay', label: 'Bay / frame' },
            {
              key: 'N_kN',
              label: 'N (kN)',
              align: 'right',
              render: (r) => fmtForce(r.N_kN),
            },
          ]}
        />
        {(vDiag?.wind_column_loads || []).length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Wind-column loads
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Edge</TableCell>
                  <TableCell align="right">Segment (m)</TableCell>
                  <TableCell align="right">Foundation (kN)</TableCell>
                  <TableCell align="right">Top chord (kN)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vDiag.wind_column_loads.map((w, i) => (
                  <TableRow key={`wc-${i}`}>
                    <TableCell>
                      {w.edge}
                      {w.x0_m != null ? ` x=${Number(w.x0_m).toFixed(1)}` : ''}
                      {w.y0_m != null ? ` y=${Number(w.y0_m).toFixed(1)}` : ''}
                    </TableCell>
                    <TableCell align="right">{Number(w.segment_length_m).toFixed(2)}</TableCell>
                    <TableCell align="right">{Number(w.foundation_kN).toFixed(2)}</TableCell>
                    <TableCell align="right">{Number(w.top_chord_kN).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </TabPanel>
    </Box>
  );
}
